export type WorkCopy = {
  hero: {
    eyebrow: string
    heading: string
    summary: string
    ownership: string
  }
  feature: {
    label: string
    heading: string
    body: string
    capabilities: string[]
    caseStudyLink: string
    servicesLink: string
  }
  outcomes: {
    label: string
    items: Array<{ value: string; label: string; calculation: string }>
  }
  conversion: {
    heading: string
    body: string
    review: string
    conversation: string
  }
  mock: MockCopy
}

export type CajankaCopy = {
  breadcrumb: {
    home: string
    work: string
    current: string
  }
  hero: {
    label: string
    heading: string
    summary: string
    ownershipLabel: string
    ownership: string
    liveLabel: string
  }
  context: {
    eyebrow: string
    heading: string
    body: string
    details: Array<{ label: string; value: string }>
  }
  system: {
    eyebrow: string
    heading: string
    body: string
    nodes: string[]
  }
  chapters: Array<{
    number: string
    eyebrow: string
    heading: string
    body: string
    points: string[]
    mockKind: Exclude<MockKind, "storefront">
  }>
  supporting: {
    eyebrow: string
    heading: string
    body: string
    items: string[]
  }
  outcomes: {
    eyebrow: string
    heading: string
    disclosure: string
    items: Array<{ value: string; label: string; calculation: string }>
  }
  conversion: {
    eyebrow: string
    heading: string
    body: string
    services: string
    review: string
    conversation: string
  }
  mock: MockCopy
}

export type MockKind = "storefront" | "accounting" | "stock" | "supplier" | "affiliate"

export type MockCopy = {
  temporaryLabel: string
  replacementNote: string
  storefront: {
    desktop: string
    mobile: string
    navigation: string[]
    product: string
  }
  accounting: {
    title: string
    source: string
    destination: string
    statuses: string[]
  }
  stock: {
    title: string
    columns: string[]
    rows: string[][]
  }
  supplier: {
    title: string
    stages: string[]
  }
  affiliate: {
    title: string
    metrics: string[]
    rows: string[]
  }
}

const MOCK_COPY: Record<"hr" | "en", MockCopy> = {
  hr: {
    temporaryLabel: "Privremeni prikaz sučelja",
    replacementNote: "Podaci su izmišljeni i služe samo za prikaz rasporeda. Zamjena stvarnim, odobrenim i redigiranim snimkama prati se u GitHub zadatku #22.",
    storefront: {
      desktop: "Koncept desktop prikaza web-trgovine",
      mobile: "Koncept mobilnog prikaza web-trgovine",
      navigation: ["Čajevi", "Pribor", "Pokloni"],
      product: "Proizvod",
    },
    accounting: {
      title: "Tok narudžbe prema računovodstvu",
      source: "WooCommerce",
      destination: "Minimax",
      statuses: ["Provjera podataka", "Priprema računa", "Sinkronizirano"],
    },
    stock: {
      title: "Smjernice za naručivanje zaliha",
      columns: ["Artikl", "Zaliha", "Prodaja", "Smjernica"],
      rows: [
        ["Čaj 01", "—", "—", "Provjeri"],
        ["Čaj 02", "—", "—", "Naruči"],
        ["Pribor 01", "—", "—", "Dovoljno"],
      ],
    },
    supplier: {
      title: "Dobavljački račun u nacrt primke",
      stages: ["Dokument", "Čitanje stavki", "Ljudski pregled", "Nacrt u Minimaxu"],
    },
    affiliate: {
      title: "Partnerski pregled",
      metrics: ["Klikovi", "Narudžbe", "Provizija"],
      rows: ["Partnerska poveznica", "Pripisane narudžbe", "Zahtjevi za isplatu"],
    },
  },
  en: {
    temporaryLabel: "Temporary interface preview",
    replacementNote: "All data is invented and shown only to communicate layout. Replacement with approved, redacted production captures is tracked in GitHub issue #22.",
    storefront: {
      desktop: "Concept desktop webshop view",
      mobile: "Concept mobile webshop view",
      navigation: ["Tea", "Accessories", "Gifts"],
      product: "Product",
    },
    accounting: {
      title: "Order flow into accounting",
      source: "WooCommerce",
      destination: "Minimax",
      statuses: ["Validate data", "Prepare invoice", "Synchronized"],
    },
    stock: {
      title: "Stock-order guidance",
      columns: ["Item", "Stock", "Sales", "Guidance"],
      rows: [
        ["Tea 01", "—", "—", "Review"],
        ["Tea 02", "—", "—", "Order"],
        ["Accessory 01", "—", "—", "Enough"],
      ],
    },
    supplier: {
      title: "Supplier invoice to stock-entry draft",
      stages: ["Document", "Read line items", "Human review", "Minimax draft"],
    },
    affiliate: {
      title: "Partner overview",
      metrics: ["Clicks", "Orders", "Commission"],
      rows: ["Affiliate link", "Attributed orders", "Payout requests"],
    },
  },
}

export const WORK_COPY: Record<"hr" | "en", WorkCopy> = {
  hr: {
    hero: {
      eyebrow: "Rad u praksi",
      heading: "Jedno stvarno poslovanje. Cijeli povezani sustav.",
      summary: "Na početku prikazujemo jedan projekt dovoljno detaljno da se može razumjeti što je izgrađeno, zašto postoji i kako podržava svakodnevni rad.",
      ownership: "Čajanka je ecommerce poslovanje u vlasništvu Zmile d.o.o. i svakodnevno posluje kao stvarna maloprodajna tvrtka. Nije predstavljena kao vanjski klijent.",
    },
    feature: {
      label: "Studija slučaja vlastitog poslovanja · Čajanka",
      heading: "Od web-trgovine do sustava koji povezuje prodaju, računovodstvo, zalihe i partnerski rast.",
      body: "Čajanka pokazuje raspon Zmileova rada: javno korisničko iskustvo, integracije između postojećih alata, automatizaciju ponavljajućih procesa i prilagođene programe izgrađene oko stvarnog poslovanja.",
      capabilities: ["Web-trgovina", "WooCommerce i Minimax", "Planiranje zaliha", "Dobavljački dokumenti", "Partnerski program", "Izvještavanje"],
      caseStudyLink: "Otvorite cijelu studiju slučaja",
      servicesLink: "Pogledajte relevantne usluge",
    },
    outcomes: {
      label: "Podržani rezultati · srpanj 2026.",
      items: [
        {
          value: "Više od 83 sata",
          label: "izbjegnute ručne izrade računa u 2026.",
          calculation: "500 narudžbi × približno 10 minuta = oko 83,3 sata.",
        },
        {
          value: "Oko jedan sat",
          label: "ručnog unosa izbjegnuto po dobavljačkoj primci",
          calculation: "Približno 60 minuta izbjegnute ručne pripreme; ljudski pregled ostaje.",
        },
        {
          value: "Približno 10%",
          label: "mjesečne prodaje pripisano partnerima",
          calculation: "Prihod pripisan partnerima ÷ ukupni mjesečni prihod od prodaje ≈ 10%.",
        },
      ],
    },
    conversion: {
      heading: "Vaše poslovanje ne mora početi ovako široko.",
      body: "Početak može biti zastarjela web-stranica, ručna primopredaja ili jedan proces koji troši previše vremena.",
      review: "Zatražite besplatne 30-minutne konzultacije",
      conversation: "Pogledajte radove",
    },
    mock: MOCK_COPY.hr,
  },
  en: {
    hero: {
      eyebrow: "Work in practice",
      heading: "One real business. A complete connected system.",
      summary: "At launch, we show one project in enough detail to understand what was built, why it exists, and how it supports daily work.",
      ownership: "Čajanka is an ecommerce business owned by Zmile d.o.o. and operated day to day as a real retail company. It is not presented as an external client.",
    },
    feature: {
      label: "Owned Business Case Study · Čajanka",
      heading: "From webshop to a system connecting sales, accounting, stock, and partner growth.",
      body: "Čajanka demonstrates the range of Zmile's work: a public customer experience, integrations between existing tools, automation of repetitive work, and custom programs shaped around a real business.",
      capabilities: ["Webshop", "WooCommerce and Minimax", "Stock planning", "Supplier documents", "Affiliate program", "Reporting"],
      caseStudyLink: "Open the full Case Study",
      servicesLink: "See the relevant Services",
    },
    outcomes: {
      label: "Supported outcomes · July 2026",
      items: [
        {
          value: "More than 83 hours",
          label: "of manual invoice creation avoided in 2026",
          calculation: "500 orders × approximately 10 minutes = about 83.3 hours.",
        },
        {
          value: "About one hour",
          label: "of manual entry avoided per supplier stock entry",
          calculation: "Approximately 60 minutes of manual preparation avoided; human review remains.",
        },
        {
          value: "Approximately 10%",
          label: "of monthly sales attributed to affiliates",
          calculation: "Affiliate-attributed revenue ÷ total monthly sales revenue ≈ 10%.",
        },
      ],
    },
    conversion: {
      heading: "Your engagement does not need to begin this broadly.",
      body: "The starting point can be an outdated website, a manual handoff, or one workflow consuming too much time.",
      review: "Request a free 30-minute consultation",
      conversation: "See the work",
    },
    mock: MOCK_COPY.en,
  },
}

export const CAJANKA_COPY: Record<"hr" | "en", CajankaCopy> = {
  hr: {
    breadcrumb: { home: "Početna", work: "Radovi", current: "Čajanka" },
    hero: {
      label: "Studija slučaja vlastitog poslovanja",
      heading: "Čajanka povezuje web-trgovinu s poslovanjem iza nje.",
      summary: "Ovo nije priča o jednom izoliranom redizajnu. To je prikaz sustava koji povezuje kupnju, narudžbe, računovodstvo, zalihe, dobavljače, partnere i odluke koje maloprodajno poslovanje donosi svaki dan.",
      ownershipLabel: "U vlasništvu Zmile d.o.o.",
      ownership: "Čajanka je ecommerce poslovanje u vlasništvu Zmile d.o.o. i svakodnevno posluje kao stvarna maloprodajna tvrtka. Na njoj gradimo, testiramo i poboljšavamo povezane sustave opisane na ovoj stranici.",
      liveLabel: "Posjetite Čajanka.hr",
    },
    context: {
      eyebrow: "Poslovni kontekst",
      heading: "Web-trgovina je ulaz u operativni sustav.",
      body: "Kupac vidi proizvode, sadržaj i jednostavan put do narudžbe. Iza toga tim mora uskladiti račun, zalihu, nabavu, partnera i izvještavanje. Ručno ponavljanje istih podataka ograničava rast i povećava mogućnost pogreške.",
      details: [
        { label: "Poslovni model", value: "Specijalizirana ecommerce maloprodaja" },
        { label: "Vlasništvo", value: "Zmile d.o.o." },
        { label: "Rad", value: "Web, integracije, automatizacija i prilagođeni sustavi" },
      ],
    },
    system: {
      eyebrow: "Jedan povezani sustav",
      heading: "Informacija se prenosi dalje bez ponovnog ručnog prepisivanja.",
      body: "Sustav je namjerno modularan: svaki dio rješava konkretan operativni problem, a zajednički tokovi ih povezuju.",
      nodes: ["Čajanka.hr", "WooCommerce", "Minimax", "Zalihe i nabava", "Dobavljači", "Partneri", "Izvještavanje"],
    },
    chapters: [
      {
        number: "01",
        eyebrow: "Web-trgovina i računovodstvo",
        heading: "WooCommerce i Minimax dijele podatke potrebne za račun.",
        body: "Podaci o kupcu, proizvodima, narudžbi i računu prelaze iz web-trgovine u računovodstveni tok. Automatizacija uklanja ponovnu ručnu izradu, dok provjere zadržavaju kontrolu nad rezultatom.",
        points: ["Provjera potrebnih podataka", "Priprema kupca i računa", "Povezivanje proizvoda i stavki", "Praćenje rezultata sinkronizacije"],
        mockKind: "accounting",
      },
      {
        number: "02",
        eyebrow: "Zalihe i dobavljači",
        heading: "Prodaja postaje smjernica za nabavu, a račun dobavljača nacrt primke.",
        body: "Trenutačna zaliha i nedavna prodaja pokazuju što bi trebalo provjeriti ili naručiti prvo. Dobavljački dokument može pripremiti stavke za ljudski pregled prije izrade nacrta u Minimaxu.",
        points: ["Zaliha uz nedavnu prodaju", "Prioriteti za ponovnu narudžbu", "Čitanje stavki dobavljačkog računa", "Pregled prije nacrta primke"],
        mockKind: "stock",
      },
      {
        number: "03",
        eyebrow: "Partnerski rast",
        heading: "Partnerski program povezuje onboarding, atribuciju, proviziju i isplatu.",
        body: "Prilagođeni program automatizira prijavu i upute partnera, poveznice i kupone, pripisivanje ponovljenih kupaca, obavijesti, obračun provizije i zahtjeve za isplatu.",
        points: ["Automatizirana prijava i onboarding", "Poveznice, kuponi i ponovljeni kupci", "Partnerski pregled narudžbi i provizije", "Povijest iskorištenja i zahtjevi za isplatu"],
        mockKind: "affiliate",
      },
    ],
    supporting: {
      eyebrow: "Dodatni operativni slojevi",
      heading: "Povezivanje se nastavlja tamo gdje odluka treba bolje podatke.",
      body: "Isti operativni pristup podržava širi skup procesa koji ne trebaju svaki vlastiti proizvod ili platformu.",
      items: ["Uvoz dobavljačkih računa", "Sinkronizacija kataloga i zaliha", "Profitabilnost narudžbi", "Izvještavanje kampanja", "Obavijesti i iznimke", "Ljudski pregled važnih odluka"],
    },
    outcomes: {
      eyebrow: "Podržani rezultati · srpanj 2026.",
      heading: "Rezultati su vezani uz stvarni proces i osnovu izračuna.",
      disclosure: "Čajanka je u vlasništvu Zmile d.o.o. Rezultati dolaze iz vlastitog svakodnevnog poslovanja, ne iz odnosa s vanjskim klijentom.",
      items: [
        {
          value: "Više od 83 sata",
          label: "ušteđeno na izdavanju računa u 2026.",
          calculation: "500 narudžbi × približno 10 minuta izbjegnute ručne izrade računa = oko 83,3 sata.",
        },
        {
          value: "Oko jedan sat",
          label: "ušteđen po unosu dobavljačke zalihe",
          calculation: "Približno 60 minuta ručne pripreme izbjegnuto je jer račun dobavljača proizvodi nacrt spreman za pregled; ljudski pregled ostaje.",
        },
        {
          value: "Približno 10%",
          label: "mjesečne prodaje pripisano partnerima",
          calculation: "Prihod narudžbi pripisanih partnerima ÷ ukupni mjesečni prihod od prodaje ≈ 10%. Privatni komercijalni iznosi nisu objavljeni.",
        },
      ],
    },
    conversion: {
      eyebrow: "Relevantan sljedeći korak",
      heading: "Pronađimo manji problem koji vrijedi povezati.",
      body: "Pogledajte što gradimo, pošaljite web-stranicu na osobnu provjeru ili opišite operativno ograničenje u razgovoru.",
      services: "Pogledajte usluge",
      review: "Zatražite besplatne 30-minutne konzultacije",
      conversation: "Povratak na radove",
    },
    mock: MOCK_COPY.hr,
  },
  en: {
    breadcrumb: { home: "Home", work: "Work", current: "Čajanka" },
    hero: {
      label: "Owned Business Case Study",
      heading: "Čajanka connects its webshop to the business behind it.",
      summary: "This is not a story about one isolated redesign. It shows a system connecting shopping, orders, accounting, stock, suppliers, partners, and the decisions a retail business makes every day.",
      ownershipLabel: "Owned by Zmile d.o.o.",
      ownership: "Čajanka is an ecommerce business owned by Zmile d.o.o. and operated day to day as a real retail company. It is where we build, test, and improve the connected systems described on this page.",
      liveLabel: "Visit Čajanka.hr",
    },
    context: {
      eyebrow: "Business context",
      heading: "The webshop is the entrance to an operating system.",
      body: "A customer sees products, content, and a straightforward path to an order. Behind that, the team must align the invoice, stock, purchasing, partner, and reporting. Re-entering the same information manually limits growth and increases the chance of error.",
      details: [
        { label: "Business model", value: "Specialist ecommerce retail" },
        { label: "Ownership", value: "Zmile d.o.o." },
        { label: "Work", value: "Web, integrations, automation, and custom systems" },
      ],
    },
    system: {
      eyebrow: "One connected system",
      heading: "Information moves forward without being manually re-entered.",
      body: "The system is deliberately modular: each part solves a specific operational problem, while shared flows connect them.",
      nodes: ["Čajanka.hr", "WooCommerce", "Minimax", "Stock and purchasing", "Suppliers", "Affiliates", "Reporting"],
    },
    chapters: [
      {
        number: "01",
        eyebrow: "Webshop and accounting",
        heading: "WooCommerce and Minimax share the information required for an invoice.",
        body: "Customer, product, order, and invoice information moves from the webshop into the accounting flow. Automation removes repeated invoice creation, while validation keeps control over the result.",
        points: ["Validate required information", "Prepare customer and invoice", "Connect products and line items", "Track synchronization outcomes"],
        mockKind: "accounting",
      },
      {
        number: "02",
        eyebrow: "Stock and suppliers",
        heading: "Sales become purchasing guidance, and a supplier invoice becomes a stock-entry draft.",
        body: "Current stock and recent sales indicate what should be reviewed or reordered first. A supplier document can prepare line items for human review before creating a Minimax draft.",
        points: ["Stock alongside recent sales", "Reorder priorities", "Read supplier-invoice line items", "Review before a stock-entry draft"],
        mockKind: "stock",
      },
      {
        number: "03",
        eyebrow: "Partner growth",
        heading: "The affiliate program connects onboarding, attribution, commission, and payout.",
        body: "A custom program automates partner signup and guidance, links and coupons, repeat-customer attribution, notifications, commission calculation, and payout requests.",
        points: ["Automated signup and onboarding", "Links, coupons, and repeat customers", "Partner view of orders and commission", "Redemption history and payout requests"],
        mockKind: "affiliate",
      },
    ],
    supporting: {
      eyebrow: "Supporting operational layers",
      heading: "Connections continue wherever a decision needs better information.",
      body: "The same operational approach supports a wider set of processes that do not each need their own product or platform.",
      items: ["Supplier-invoice imports", "Catalog and inventory synchronization", "Order profitability", "Campaign reporting", "Notifications and exceptions", "Human review for consequential decisions"],
    },
    outcomes: {
      eyebrow: "Supported outcomes · July 2026",
      heading: "Results stay attached to the real workflow and their calculation basis.",
      disclosure: "Čajanka is owned by Zmile d.o.o. These outcomes come from its own daily operations, not an external-client relationship.",
      items: [
        {
          value: "More than 83 hours",
          label: "saved on invoicing in 2026 so far",
          calculation: "500 orders × approximately 10 minutes of avoided manual invoice creation = about 83.3 hours.",
        },
        {
          value: "About one hour",
          label: "saved per supplier stock entry",
          calculation: "Approximately 60 minutes of manual preparation is avoided because the supplier invoice produces a review-ready draft; human review remains.",
        },
        {
          value: "Approximately 10%",
          label: "of monthly sales attributed to affiliates",
          calculation: "Revenue from affiliate-attributed orders ÷ total monthly sales revenue ≈ 10%. Private commercial totals are not published.",
        },
      ],
    },
    conversion: {
      eyebrow: "A relevant next step",
      heading: "Find a smaller problem worth connecting.",
      body: "See what we build, send your website for a personal review, or describe an operational constraint in a conversation.",
      services: "See the Services",
      review: "Request a free 30-minute consultation",
      conversation: "Back to work",
    },
    mock: MOCK_COPY.en,
  },
}
